import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { Tab, UserState } from './UserState';
import {
  get as getUser,
  User,
  update as updateUser,
  getDiff,
  RenderRegion,
  getDelayBetweenUrls,
  RenderLog,
  ScrollSettings,
  ExtractHiddenCssLocation,
} from '../../../services/UsersService';
import { WritableDraft } from 'immer/dist/internal';
import { RejectError, RejectErrorType } from '../../error/Error';
import { APIError } from '../../../services/HttpService';
import { createDefaultMatchers, SliceStatus } from '../../BaseState';

const initialState: UserState = {
  originalUser: null,
  user: null,
  grantsHeldAtLoad: [],
  activeTab: Tab.general,
  pendings: [],
  status: SliceStatus.idle,
  error: null,
};

export const get = createAsyncThunk<User, number, { rejectValue: RejectError }>(
  'user/get',
  async (id: number, { rejectWithValue }) => {
    try {
      const user: User = await getUser(id);

      return user;
    } catch (error) {
      if (error instanceof APIError)
        return rejectWithValue({ message: error.message, code: error.statusCode, type: RejectErrorType.API });

      throw error;
    }
  }
);

interface UpdateUserData {
  readonly user: User;
  readonly originalUser: User;
}
export const update = createAsyncThunk<User, UpdateUserData, { rejectValue: RejectError }>(
  'user/update',
  async (updateUserData: UpdateUserData, { rejectWithValue }) => {
    try {
      const user: User = await updateUser(
        updateUserData.user.id,
        getDiff(updateUserData.user, updateUserData.originalUser)
      );

      return user;
    } catch (error) {
      if (error instanceof APIError)
        return rejectWithValue({ message: error.message, code: error.statusCode, type: RejectErrorType.API });

      throw error;
    }
  }
);

const createActions = (builder: ActionReducerMapBuilder<UserState>) => {
  builder.addCase(get.fulfilled, (draft, action) => {
    draft.user = action.payload;
    draft.originalUser = action.payload;
    draft.grantsHeldAtLoad = action.payload.availableFeatures;
  });
  builder.addCase(update.fulfilled, (draft, action) => {
    draft.user = action.payload;
    draft.originalUser = action.payload;
  });

  createDefaultMatchers([get.typePrefix, update.typePrefix], builder);
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCSM: (draft: WritableDraft<UserState>, action: PayloadAction<string>) => {
      if (draft.user) {
        draft.user.csmId = action.payload;
      }
    },
    clearUser: (draft: WritableDraft<UserState>) => {
      draft.user = null;
    },
    setActiveTab: (draft: WritableDraft<UserState>, action: PayloadAction<Tab>) => {
      draft.activeTab = action.payload;
    },
    setAdminEmail: (draft: WritableDraft<UserState>, action: PayloadAction<string>) => {
      if (draft.user) {
        draft.user.adminEmail = action.payload;
      }
    },
    setTrackingCodeInstalled: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.trackingCodeInstalled = action.payload;
      }
    },
    setCompanyName: (draft: WritableDraft<UserState>, action: PayloadAction<string>) => {
      if (draft.user) {
        draft.user.companyName = action.payload;
      }
    },
    setHasUnlimRenders: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        if (action.payload) {
          const now = new Date();
          draft.user.hasUnlimRenders = true;
          draft.user.unlimRendersUntil = new Date(now.setDate(now.getDate() + 7)).toISOString();
          draft.user.renderCounter = 0;
        } else {
          draft.user.hasUnlimRenders = false;
          draft.user.unlimRendersUntil = null;
          draft.user.renderCounter = draft.originalUser!.renderCounter;
        }
      }
    },
    setUnlimRendersUntil: (draft: WritableDraft<UserState>, action: PayloadAction<Date>) => {
      if (draft.user) {
        draft.user.hasUnlimRenders = true;
        draft.user.unlimRendersUntil = action.payload.toISOString();
        draft.user.renderCounter = 0;
      }
    },
    setBetaTester: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.betaTester = action.payload;
      }
    },
    setForceHTTPS: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.forceHTTPS = action.payload;
      }
    },
    setCustomViewPort: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        if (action.payload) {
          draft.user.settings.customViewPort = { width: 0, height: 0 };
        } else {
          draft.user.settings.customViewPort = null;
        }
      }
    },
    setCustomViewPortWidth: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        if (!draft.user.settings.customViewPort) draft.user.settings.customViewPort = { width: 0, height: 0 };
        draft.user.settings.customViewPort.width = action.payload;
      }
    },
    setCustomViewPortHeight: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        if (!draft.user.settings.customViewPort) draft.user.settings.customViewPort = { width: 0, height: 0 };
        draft.user.settings.customViewPort.height = action.payload;
      }
    },
    setCustomMobileViewPort: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        if (action.payload) {
          draft.user.settings.customMobileViewport = { width: 0, height: 0 };
        } else {
          draft.user.settings.customMobileViewport = null;
        }
      }
    },
    setCustomMobileViewPortWidth: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        if (!draft.user.settings.customMobileViewport)
          draft.user.settings.customMobileViewport = { width: 0, height: 0 };
        draft.user.settings.customMobileViewport.width = action.payload;
      }
    },
    setCustomMobileViewPortHeight: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        if (!draft.user.settings.customMobileViewport)
          draft.user.settings.customMobileViewport = { width: 0, height: 0 };
        draft.user.settings.customMobileViewport.height = action.payload;
      }
    },
    setLoadImages: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.loadImages = action.payload;
      }
    },
    setRenderRegion: (draft: WritableDraft<UserState>, action: PayloadAction<RenderRegion>) => {
      if (draft.user) {
        draft.user.settings.renderRegion = action.payload;
      }
    },
    setSkipCustomElementsForcePolyfill: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.skipCustomElementsForcePolyfill = action.payload;
      }
    },
    setRemoveStyleTags: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.removeStyleTags = action.payload;
      }
    },
    setRemoveURLHash: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.removeURLHash = action.payload;
      }
    },
    setEnableServiceWorker: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.enableServiceWorker = action.payload;
      }
    },
    setPageLoadTimeout: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        draft.user.settings.pageLoadTimeout = action.payload;
      }
    },
    setMobileAdaptive: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.mobileAdaptive = action.payload;
      }
    },
    setRecachingDisabled: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.recachingDisabled = action.payload;
      }
    },
    setCustomRecacheDelay: (draft: WritableDraft<UserState>, action: PayloadAction<number | null>) => {
      if (draft.user) {
        if (action.payload !== null) {
          draft.user.environment.customRecacheDelay = action.payload;
          draft.user.delayBetweenURLs = action.payload;
        } else {
          draft.user.environment.customRecacheDelay = null;
          draft.user.delayBetweenURLs = getDelayBetweenUrls(
            draft.user.environment.cacheFreshness,
            draft.user.allUrlCount
          );
        }
      }
    },
    setParseShadowDom: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.parseShadowDom = action.payload;
      }
    },
    setRestoreSearchParams: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.normalizeUri = {
          ...draft.user.settings.normalizeUri,
          restoreSearchParams: action.payload,
        };
      }
    },
    setEncodedBrackets: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.normalizeUri = {
          ...draft.user.settings.normalizeUri,
          encodedBrackets: action.payload,
        };
      }
    },
    setFixHeadTag: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.fixHeadTag = action.payload;
      }
    },
    setRemovePrefetch: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.removePrefetch = action.payload;
      }
    },
    setRemovePreload: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.removePreload = action.payload;
      }
    },
    setRetryTimedoutRenders: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.retryTimedoutRenders = action.payload;
      }
    },
    setRetryDirtyRenders: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.retryDirtyRenders = action.payload;
      }
    },
    setPreCheckStatus: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.preCheckStatus = action.payload;
      }
    },
    setUserPausedRecaching: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.userPausedRecaching = action.payload;
      }
    },
    setIgnoreAllQueryParams: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.ignoreAllQueryParams = action.payload;
      }
    },
    setWhitelistQueryParams: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.whitelistQueryParams = action.payload;
      }
    },
    setManualReactivate: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.manualReactivate = action.payload;
      }
    },
    setExtractHiddenCss: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.extractHiddenCss = action.payload;
      }
    },
    setOnlyServeCacheHits: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.onlyServeCacheHits = action.payload;
      }
    },
    setAddBlockedResources: (draft: WritableDraft<UserState>, action: PayloadAction<Array<string>>) => {
      if (draft.user) {
        draft.user.settings.addBlockedResources = action.payload;
      }
    },
    setRemoveBlockedResources: (draft: WritableDraft<UserState>, action: PayloadAction<Array<string>>) => {
      if (draft.user) {
        draft.user.settings.removeBlockedResources = action.payload;
      }
    },
    setRenderLogSettings: (draft: WritableDraft<UserState>, action: PayloadAction<RenderLog[]>) => {
      if (draft.user) {
        draft.user.settings.renderLogs = action.payload;
      }
    },
    setCacheFreshness: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        draft.user.environment.cacheFreshness = action.payload;
      }
    },
    setScrollSettings: (draft: WritableDraft<UserState>, action: PayloadAction<ScrollSettings>) => {
      if (draft.user) {
        draft.user.settings.scroll = action.payload;
      }
    },
    setExtractHiddenCssLocation: (draft: WritableDraft<UserState>, action: PayloadAction<ExtractHiddenCssLocation>) => {
      if (draft.user) {
        draft.user.settings.extractHiddenCssLocation = action.payload;
      }
    },
    setMinimumBodyLength: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        draft.user.environment.minimumBodyLength = action.payload;
      }
    },
    setForceApiAccess: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.forceApiAccess = action.payload;
      }
    },
    setSortQueryParameters: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.normalizeUri = {
          ...draft.user.settings.normalizeUri,
          sortQueryParameters: action.payload,
        };
      }
    },
    setScrollToBottomAfterPageLoad: (draft: WritableDraft<UserState>, action: PayloadAction<boolean>) => {
      if (draft.user) {
        draft.user.settings.scrollToBottomAfterPageLoad = action.payload;
      }
    },
    setSlowRenderTriggerSeconds: (draft: WritableDraft<UserState>, action: PayloadAction<number>) => {
      if (draft.user) {
        draft.user.environment.slowRenderTriggerSeconds = action.payload;
      }
    },
    setAvailableFeatures: (draft: WritableDraft<UserState>, action: PayloadAction<string[]>) => {
      if (draft.user) {
        draft.user.availableFeatures = action.payload;
      }
    },
  },
  extraReducers: createActions,
});

export const {
  clearUser,
  setActiveTab,
  setAdminEmail,
  setCompanyName,
  setTrackingCodeInstalled,
  setCSM,
  setHasUnlimRenders,
  setUnlimRendersUntil,
  setBetaTester,
  setForceHTTPS,
  setCustomViewPort,
  setCustomViewPortWidth,
  setCustomViewPortHeight,
  setCustomMobileViewPort,
  setCustomMobileViewPortWidth,
  setCustomMobileViewPortHeight,
  setLoadImages,
  setRenderRegion,
  setSkipCustomElementsForcePolyfill,
  setRemoveStyleTags,
  setRemoveURLHash,
  setEnableServiceWorker,
  setPageLoadTimeout,
  setMobileAdaptive,
  setRecachingDisabled,
  setCustomRecacheDelay,
  setParseShadowDom,
  setRestoreSearchParams,
  setEncodedBrackets,
  setFixHeadTag,
  setRemovePrefetch,
  setRemovePreload,
  setRetryTimedoutRenders,
  setRetryDirtyRenders,
  setPreCheckStatus,
  setUserPausedRecaching,
  setIgnoreAllQueryParams,
  setWhitelistQueryParams,
  setManualReactivate,
  setExtractHiddenCss,
  setOnlyServeCacheHits,
  setAddBlockedResources,
  setRemoveBlockedResources,
  setRenderLogSettings,
  setCacheFreshness,
  setScrollSettings,
  setExtractHiddenCssLocation,
  setMinimumBodyLength,
  setForceApiAccess,
  setSortQueryParameters,
  setScrollToBottomAfterPageLoad,
  setSlowRenderTriggerSeconds,
  setAvailableFeatures,
} = userSlice.actions;
export const userState = (state: RootState): UserState => state.user;
export default userSlice.reducer;
